"use client";

// DS component @tb/date-picker, adapted to a modal Dialog instead of a Popover, with
// year/month navigation, `minDate` support and a fixed 42-cell grid.
//
// The field is typed, not only picked (2026-09-29): a date is eight digits, and a birth date
// forty years back was a dozen taps through the year list. The text takes a `dd/mm/aaaa` mask —
// the reference's own `maskDate` for the event date — and the calendar icon opens the same
// calendar as before. One component, so every date in the product behaves the same.

import * as React from "react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { format } from "date-fns";

import { isCalendarKey, moveCalendarFocus } from "@/lib/calendar-navigation";
import { civilDateToLocalDate } from "@/lib/civil-date";
// TODO(DS): replace with @tb/icon when DS icon set is available
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

type View = "days" | "months" | "years";

export interface DatePickerProps {
  id?: string;
  title: string;
  value?: Date;
  onValueChange: (date: Date) => void;
  /**
   * The typed text no longer names a date the field accepts (erased, incomplete or impossible).
   * Without it the field cannot be emptied by typing: leaving it puts back the last date chosen.
   */
  onClear?: () => void;
  onBlur?: () => void;
  invalid?: boolean;
  minDate?: Date;
  defaultMonth?: Date;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  dialogClassName?: string;
  dialogOverlayClassName?: string;
  "aria-describedby"?: string;
  enableYearJump?: boolean;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** The reference's `maskDate`: digits only, slashes after the day and the month. */
export function maskDateInput(input: string): string {
  const digits = input.replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  return [day, month, year].filter((part) => part !== "").join("/");
}

/**
 * A complete `dd/mm/aaaa` that names a real day on or after the floor, or `null`. "31/02/2026"
 * is refused rather than rolled over to March, as `new Date` would.
 */
export function parseTypedDate(text: string, minDate?: Date): Date | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (match === null) return null;

  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1900) return null;

  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  if (minDate && date < startOfDay(minDate)) return null;

  return date;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker({
  id,
  title,
  value,
  onValueChange,
  onClear,
  onBlur,
  invalid,
  minDate,
  defaultMonth,
  placeholder = "dd/mm/aaaa",
  disabled = false,
  className,
  dialogClassName,
  dialogOverlayClassName,
  "aria-describedby": ariaDescribedBy,
  enableYearJump = true,
}, ref) {
  const normalizedMinDate = minDate ? startOfDay(minDate) : undefined;
  // A value the floor already refuses does not open the calendar on its own month: a list saved
  // with an old date would open on that month with every day disabled, and paging forward to a
  // choosable one is the only way out. It opens on the first month that has days to choose.
  const valueIsChoosable = value !== undefined && (!normalizedMinDate || value >= normalizedMinDate);
  const focusDate = (valueIsChoosable ? value : undefined) ?? normalizedMinDate ?? defaultMonth ?? new Date(1995, 0, 1);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("days");
  const [cursor, setCursor] = useState(() => ({ y: focusDate.getFullYear(), m: focusDate.getMonth() }));

  /**
   * The day holding the tab stop — roving `tabindex`, added locally (ds-variant-candidates T12).
   *
   * One stop per button would put thirty-one stops between the title and the next field, so the
   * grid is a single stop and the keyboard moves inside it. `null` means nobody has navigated
   * yet: the stop sits on the chosen day, or on the 1st when there is no choice.
   */
  const [focusedDay, setFocusedDay] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  /*
   * What the field shows. It follows `value` when the value changes from outside (a date picked,
   * a form reset), and otherwise keeps what the person is typing — adjusted during render, not
   * in an effect, so the field never shows a frame of the old text.
   */
  const formattedValue = value ? format(value, "dd/MM/yyyy") : "";
  const [text, setText] = useState(formattedValue);
  const [previousValue, setPreviousValue] = useState(formattedValue);
  // The value this field just handed over: when it comes back, it is not news to the text.
  const [echo, setEcho] = useState<string | null>(null);
  const [leftIncomplete, setLeftIncomplete] = useState(false);
  if (formattedValue !== previousValue) {
    setPreviousValue(formattedValue);
    setEcho(null);
    if (formattedValue !== echo) {
      setText(formattedValue);
      setLeftIncomplete(false);
    }
  }

  // Complete but not a day this field takes, or left half-typed: the field says so at once,
  // before any submit does.
  const typedInvalid =
    text !== "" && parseTypedDate(text, minDate) === null && (text.length === 10 || leftIncomplete);

  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    const next = maskDateInput(event.target.value);
    setText(next);
    setLeftIncomplete(false);

    const date = parseTypedDate(next, minDate);
    if (date !== null) {
      setEcho(format(date, "dd/MM/yyyy"));
      onValueChange(date);
      return;
    }

    if (onClear && formattedValue !== "") {
      // The half-typed text stays on screen while the value empties.
      setEcho("");
      onClear();
    }
  }

  function handleTextBlur() {
    if (parseTypedDate(text, minDate) === null && text !== "") {
      if (onClear) setLeftIncomplete(true);
      else setText(formattedValue);
    }
    // Without `onClear`, an erased field is not a cleared value: it goes back to the date chosen.
    if (text === "" && !onClear) setText(formattedValue);
    onBlur?.();
  }

  function handleTextKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // The date-field convention for opening its calendar from the keyboard.
    if (event.altKey && event.key === "ArrowDown") {
      event.preventDefault();
      handleOpenChange(true);
    }
  }

  function close() {
    setOpen(false);
    setView("days");
    setFocusedDay(null);
    onBlur?.();
  }

  function handleOpenChange(next: boolean) {
    if (next) {
      setCursor({ y: focusDate.getFullYear(), m: focusDate.getMonth() });
      setView("days");
      setFocusedDay(null);
      setOpen(true);
    } else {
      close();
    }
  }

  function navigateMonth(delta: number) {
    setCursor((c) => {
      let m = c.m + delta;
      let y = c.y;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { y, m };
    });
  }

  function pickDay(day: number) {
    onValueChange(new Date(cursor.y, cursor.m, day));
    close();
  }

  const isAtMinMonth =
    !!normalizedMinDate && cursor.y === normalizedMinDate.getFullYear() && cursor.m === normalizedMinDate.getMonth();

  /** The day with the tab stop: the navigated one, the chosen one, or the first. */
  const selectedDayInView =
    value && value.getFullYear() === cursor.y && value.getMonth() === cursor.m
      ? value.getDate()
      : null;
  const tabStopDay = focusedDay ?? selectedDayInView ?? 1;

  /**
   * Takes focus to the navigated day once the grid redraws. Runs on a month change too, because
   * PageUp and PageDown move the month **and** the day.
   */
  useEffect(() => {
    if (focusedDay === null) return;

    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-day="${focusedDay}"]`)
      ?.focus();
  }, [focusedDay, cursor.y, cursor.m]);

  function handleGridKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!isCalendarKey(event.key)) return;

    const target = moveCalendarFocus(
      { year: cursor.y, month: cursor.m + 1, day: tabStopDay },
      event.key,
    );

    // Before the allowed minimum there is nowhere to go: focus stays where it is, instead of
    // landing on an inert day that cannot be chosen.
    if (normalizedMinDate && civilDateToLocalDate(target) < normalizedMinDate) return;

    event.preventDefault();
    setCursor({ y: target.year, m: target.month - 1 });
    setFocusedDay(target.day);
  }

  const firstWeekday = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const prevMonthDays = new Date(cursor.y, cursor.m, 0).getDate();

  const cells: ReactNode[] = [];
  for (let i = firstWeekday - 1; i >= 0; i--) {
    cells.push(
      <span key={`p${i}`} className="grid aspect-square min-h-9 place-content-center rounded-full text-[13px] tabular-nums text-[var(--fg-subtle)] opacity-40">
        {prevMonthDays - i}
      </span>,
    );
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const cellDate = new Date(cursor.y, cursor.m, day);
    const isSelected = value ? sameDay(value, cellDate) : false;
    const isToday = sameDay(cellDate, new Date());
    const isDisabled = normalizedMinDate ? cellDate < normalizedMinDate : false;
    cells.push(
      isDisabled ? (
        <span
          key={`d${day}`}
          aria-disabled="true"
          className="grid aspect-square min-h-9 place-content-center rounded-full text-[13px] tabular-nums text-[var(--fg-subtle)] opacity-40"
        >
          {day}
        </span>
      ) : (
        <button
          key={`d${day}`}
          type="button"
          data-day={day}
          tabIndex={day === tabStopDay ? 0 : -1}
          aria-current={isToday ? "date" : undefined}
          onClick={() => pickDay(day)}
          className={cn(
            "grid aspect-square min-h-9 cursor-pointer place-content-center rounded-full text-[13px] tabular-nums transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-green-tint)]",
            isSelected ? "bg-primary text-[var(--tb-porcelain)]" : "text-foreground",
            isToday && !isSelected && "shadow-[inset_0_0_0_1px_var(--tb-border-strong)]",
          )}
        >
          {day}
        </button>
      ),
    );
  }
  // Pad to 42 cells (6 rows) for stable grid height across months.
  const trailing = 42 - cells.length;
  for (let i = 1; i <= trailing; i++) {
    cells.push(
      <span key={`t${i}`} className="grid aspect-square min-h-9 place-content-center rounded-full text-[13px] tabular-nums text-[var(--fg-subtle)] opacity-40">
        {i}
      </span>,
    );
  }

  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= currentYear - 100; y--) years.push(y);

  return (
    <div className="relative min-w-0">
      <div
        data-invalid={invalid || typedInvalid || undefined}
        className={cn(
          "flex h-11 w-full min-w-0 items-center justify-between gap-2.5 border-b-[1.5px] bg-transparent py-2.5 text-left font-sans text-[17px] text-foreground transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]",
          invalid || typedInvalid
            ? "border-b-destructive"
            : open
              ? "border-b-2 border-b-primary"
              : cn(
                  "border-b-[color:var(--tb-border-strong)] focus-within:border-b-2 focus-within:border-b-primary",
                  // The field is a `<div>`, so `enabled:` has nothing to match: the hover is
                  // withheld by the prop instead. A locked field does not answer the pointer
                  // (ruling of 2026-10-06).
                  !disabled && "hover:border-b-tb-oxide",
                ),
          disabled && "border-dashed text-[var(--fg-subtle)]",
          className,
        )}
      >
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid || typedInvalid || undefined}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onKeyDown={handleTextKeyDown}
          className="min-w-0 flex-1 bg-transparent p-0 [font:inherit] text-inherit outline-none placeholder:text-[var(--fg-subtle)] disabled:cursor-not-allowed"
        />
        <button
          type="button"
          aria-label={`Abrir calendário: ${title}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={disabled}
          onClick={() => handleOpenChange(true)}
          // 44px of target on a 18px glyph, pulled back into the field's own edge.
          // `hover:enabled:`: a locked field's trigger must not answer the pointer (2026-10-06).
          className="-my-2.5 -mr-3 grid size-11 shrink-0 cursor-pointer place-content-center text-[var(--fg-muted)] outline-none transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:enabled:text-foreground focus-visible:shadow-[var(--focus-ring)] disabled:cursor-not-allowed"
        >
          <Calendar className="size-[18px]" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn("w-[336px] p-5 pb-[22px]", dialogClassName)}
          overlayClassName={dialogOverlayClassName}
          // The host panel runs Lenis, which captures the wheel on the whole window and moved
          // the page behind this dialog while the year list scrolled. Same mark `drawer.tsx`
          // carries; inert wherever Lenis is not running.
          data-lenis-prevent=""
        >
          <DialogTitle className="mb-2.5 text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-muted)]">
            {title}
          </DialogTitle>

          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              disabled={!enableYearJump}
              onClick={() => enableYearJump && setView(view === "days" ? "years" : "days")}
              className={cn(
                "-ml-2 flex items-center gap-1.5 rounded-[var(--tb-radius-xs)] px-2 py-1.5 font-display text-[18px] text-foreground transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]",
                enableYearJump ? "cursor-pointer hover:bg-[var(--tb-oxide-tint)]" : "cursor-default",
              )}
            >
              {view === "years" ? "Selecione o ano" : view === "months" ? cursor.y : `${MONTHS[cursor.m]} ${cursor.y}`}
              {enableYearJump && (
                <ChevronDown className={cn("size-4 text-[var(--fg-muted)] transition-transform duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]", view !== "days" && "rotate-180")} />
              )}
            </button>
            {view === "days" && (
              <div className="flex gap-0.5">
                <button
                  type="button"
                  aria-label="Mês anterior"
                  disabled={isAtMinMonth}
                  onClick={() => navigateMonth(-1)}
                  className="grid size-10 cursor-pointer place-content-center rounded-[var(--tb-radius-xs)] text-foreground transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-oxide-tint)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="size-[18px]" />
                </button>
                <button
                  type="button"
                  aria-label="Próximo mês"
                  onClick={() => navigateMonth(1)}
                  className="grid size-10 cursor-pointer place-content-center rounded-[var(--tb-radius-xs)] text-foreground transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-oxide-tint)]"
                >
                  <ChevronRight className="size-[18px]" />
                </button>
              </div>
            )}
          </div>

          {view === "days" && (
            <div
              ref={gridRef}
              onKeyDown={handleGridKeyDown}
              className="grid grid-cols-7 gap-0.5"
            >
              {WEEKDAYS.map((weekday, index) => (
                <span key={index} className="py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-subtle)]">
                  {weekday}
                </span>
              ))}
              {cells}
            </div>
          )}

          {enableYearJump && view === "months" && (
            <div className="grid grid-cols-3 gap-1.5">
              {MONTHS.map((month, index) => (
                <button
                  key={month}
                  type="button"
                  onClick={() => {
                    setCursor((c) => ({ ...c, m: index }));
                    setView("days");
                  }}
                  className={cn(
                    "min-h-11 cursor-pointer rounded-full text-[13px] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-green-tint)]",
                    index === cursor.m ? "bg-primary text-[var(--tb-porcelain)]" : "text-foreground",
                  )}
                >
                  {month.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {enableYearJump && view === "years" && (
            <div className="grid max-h-[232px] grid-cols-4 gap-1.5 overflow-y-auto">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => {
                    setCursor((c) => ({ ...c, y: year }));
                    setView("months");
                  }}
                  className={cn(
                    "min-h-11 cursor-pointer rounded-full text-[13px] tabular-nums transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-green-tint)]",
                    year === cursor.y ? "bg-primary text-[var(--tb-porcelain)]" : "text-foreground",
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});
DatePicker.displayName = "DatePicker";
